import type { PerformanceAlertFilters } from '@dashhudson/api-client/library/model';
import type { ConditionGroup, ConditionItem } from '@dashhudson/dashing-ui/condition-builder';

import {
  createConditionGroup,
  createConditionItem,
} from '@dashhudson/dashing-ui/condition-builder';

import type { ContentTagAction } from '@/app/settings/types/content-automation';

export const PERFORMANCE_ALERT_FILTER_KEY = {
  KEYWORD: 'keyword',
  CONTENT_TAGS: 'content_tags',
  SENTIMENT: 'sentiment',
  DATE_PUBLISHED: 'date_published',
} as const;

type PerformanceAlertSentimentValue = 'positive' | 'negative' | 'neutral';

type PerformanceAlertFilterOperator = 'AND' | 'OR';

interface PerformanceAlertKeywordFilterItem {
  key: typeof PERFORMANCE_ALERT_FILTER_KEY.KEYWORD;
  value: string;
}

interface PerformanceAlertSentimentFilterItem {
  key: typeof PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT;
  value: PerformanceAlertSentimentValue;
}

interface PerformanceAlertDatePublishedFilterItem {
  key: typeof PERFORMANCE_ALERT_FILTER_KEY.DATE_PUBLISHED;
  value: { startDate: string; endDate: string };
}

interface PerformanceAlertContentTagFilterItem {
  key: typeof PERFORMANCE_ALERT_FILTER_KEY.CONTENT_TAGS;
  value: number[];
}

type PerformanceAlertFilterItem =
  | PerformanceAlertContentTagFilterItem
  | PerformanceAlertKeywordFilterItem
  | PerformanceAlertSentimentFilterItem
  | PerformanceAlertDatePublishedFilterItem;

interface FilterTree {
  operator: PerformanceAlertFilterOperator;
  items: (FilterTree | PerformanceAlertFilterItem)[];
}

interface CompiledFilterGroup {
  groupOperator: PerformanceAlertFilterOperator;
  items: PerformanceAlertFilterItem[];
}

type DateRangeItemValue = { range?: { start?: Date | string; end?: Date | string } };

export function createEmptyFilterGroup(): ConditionGroup {
  return createConditionGroup({ groupOperator: 'AND', itemOperator: 'OR' });
}

export function stripSentimentItems(groups: ConditionGroup[]): ConditionGroup[] {
  return groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.key !== PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT),
  }));
}

export function hasSentimentFilterItems(groups: ConditionGroup[]): boolean {
  return groups.some((group) =>
    group.items.some((item) => item.key === PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT),
  );
}

function normalizeOperator(operator: unknown): PerformanceAlertFilterOperator {
  return operator === 'OR' ? 'OR' : 'AND';
}

function toIsoString(value: Date | string | undefined): string | undefined {
  if (value == null) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function isSentimentValue(value: unknown): value is PerformanceAlertSentimentValue {
  return value === 'positive' || value === 'negative' || value === 'neutral';
}

function getSentimentValues(value: unknown): PerformanceAlertSentimentValue[] {
  const candidates = Array.isArray(value) ? value : value == null ? [] : [value];
  return candidates.filter(isSentimentValue);
}

function isFilterTree(item: unknown): item is FilterTree {
  return item != null && typeof item === 'object' && 'operator' in item && 'items' in item;
}

export function isPerformanceAlertFilterItemComplete(item: ConditionItem): boolean {
  switch (item.key) {
    case PERFORMANCE_ALERT_FILTER_KEY.CONTENT_TAGS:
      return Array.isArray(item.value) && item.value.length > 0;
    case PERFORMANCE_ALERT_FILTER_KEY.KEYWORD:
      return typeof item.value === 'string' && item.value.trim().length > 0;
    case PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT:
      return getSentimentValues(item.value).length > 0;
    case PERFORMANCE_ALERT_FILTER_KEY.DATE_PUBLISHED: {
      const range = (item.value as DateRangeItemValue | undefined)?.range;
      return toIsoString(range?.start) != null && toIsoString(range?.end) != null;
    }
    default:
      return false;
  }
}

function compileFilterItems(item: ConditionItem): PerformanceAlertFilterItem[] {
  switch (item.key) {
    case PERFORMANCE_ALERT_FILTER_KEY.CONTENT_TAGS:
      return [
        {
          key: PERFORMANCE_ALERT_FILTER_KEY.CONTENT_TAGS,
          value: (item.value as ContentTagAction[]).map((tag) => tag.id),
        },
      ];
    case PERFORMANCE_ALERT_FILTER_KEY.KEYWORD:
      return [{ key: PERFORMANCE_ALERT_FILTER_KEY.KEYWORD, value: (item.value as string).trim() }];
    case PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT:
      return getSentimentValues(item.value).map((value) => ({
        key: PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT,
        value,
      }));
    case PERFORMANCE_ALERT_FILTER_KEY.DATE_PUBLISHED: {
      const range = (item.value as DateRangeItemValue).range;
      const start = toIsoString(range?.start);
      const end = toIsoString(range?.end);
      if (start == null || end == null) return [];
      return [
        {
          key: PERFORMANCE_ALERT_FILTER_KEY.DATE_PUBLISHED,
          value: { startDate: start, endDate: end },
        },
      ];
    }
    default:
      return [];
  }
}

function decompileFilterItem(raw: PerformanceAlertFilterItem): ConditionItem | null {
  switch (raw.key) {
    case PERFORMANCE_ALERT_FILTER_KEY.CONTENT_TAGS:
      return createConditionItem(
        PERFORMANCE_ALERT_FILTER_KEY.CONTENT_TAGS,
        raw.value.map((id) => ({ id })),
      );
    case PERFORMANCE_ALERT_FILTER_KEY.KEYWORD:
      return createConditionItem(PERFORMANCE_ALERT_FILTER_KEY.KEYWORD, raw.value);
    case PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT:
      return createConditionItem(PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT, [raw.value]);
    case PERFORMANCE_ALERT_FILTER_KEY.DATE_PUBLISHED:
      return createConditionItem(PERFORMANCE_ALERT_FILTER_KEY.DATE_PUBLISHED, {
        preset: 'CUSTOM',
        range: { start: new Date(raw.value.startDate), end: new Date(raw.value.endDate) },
      });
    default:
      return null;
  }
}

function mergeGroupSentimentItems(items: ConditionItem[]): ConditionItem[] {
  const merged: ConditionItem[] = [];
  let sentimentItem: ConditionItem | undefined;
  for (const item of items) {
    if (item.key !== PERFORMANCE_ALERT_FILTER_KEY.SENTIMENT) {
      merged.push(item);
    } else if (sentimentItem) {
      sentimentItem.value = [
        ...new Set([
          ...(sentimentItem.value as PerformanceAlertSentimentValue[]),
          ...getSentimentValues(item.value),
        ]),
      ];
    } else {
      sentimentItem = item;
      merged.push(item);
    }
  }
  return merged;
}

function buildFilterTree(groups: CompiledFilterGroup[]): FilterTree | undefined {
  const [result] = groups.reduce<
    [null, null, null] | [FilterTree, FilterTree, null] | [FilterTree, FilterTree, FilterTree]
  >(
    (acc, group) => {
      const [rootFilter, previousFilter, previousFilterParent] = acc;
      const filter: FilterTree = { operator: 'OR', items: [...group.items] };

      if (rootFilter === null) {
        return [filter, filter, null];
      }

      if (previousFilterParent === null) {
        if (rootFilter.operator === 'OR') {
          const newRoot: FilterTree = {
            operator: group.groupOperator,
            items: [rootFilter, filter],
          };
          return [newRoot, filter, newRoot];
        }
        throw new Error(`Invalid root filter operator: ${rootFilter.operator}`);
      }

      if (group.groupOperator === 'OR') {
        if (rootFilter.operator === 'OR') {
          (rootFilter.items as FilterTree[]).push(filter);
          return [rootFilter, filter, rootFilter];
        }
        const newRoot: FilterTree = { operator: 'OR', items: [rootFilter, filter] };
        return [newRoot, filter, newRoot];
      }

      if (previousFilterParent.operator === 'OR') {
        const newAnd: FilterTree = { operator: 'AND', items: [previousFilter, filter] };
        (previousFilterParent.items as FilterTree[]).splice(-1, 1, newAnd);
        return [rootFilter, filter, newAnd];
      }
      (previousFilterParent.items as FilterTree[]).push(filter);
      return [rootFilter, filter, previousFilterParent];
    },
    [null, null, null],
  );

  return result ?? undefined;
}

export function compilePerformanceAlertFilters(
  groups: ConditionGroup[],
): PerformanceAlertFilters | undefined {
  const compiledGroups = groups
    .map<CompiledFilterGroup>((group) => ({
      groupOperator: normalizeOperator(group.groupOperator),
      items: group.items.filter(isPerformanceAlertFilterItemComplete).flatMap(compileFilterItems),
    }))
    .filter((group) => group.items.length > 0);

  return buildFilterTree(compiledGroups) as PerformanceAlertFilters | undefined;
}

export function decompilePerformanceAlertFilters(
  filters?: PerformanceAlertFilters | null,
): ConditionGroup[] {
  if (!filters?.items?.length) return [createEmptyFilterGroup()];

  const groups: ConditionGroup[] = [];
  const root: FilterTree = {
    operator: normalizeOperator(filters.operator),
    items: filters.items as FilterTree['items'],
  };
  const stack: [FilterTree, PerformanceAlertFilterOperator][] = [[root, 'OR']];

  while (stack.length > 0) {
    const [filter, groupOperator] = stack.pop()!;

    const filterGroup = filter.items.reduceRight(
      (acc, item, index) => {
        if (!isFilterTree(item) && filter.operator === 'OR') {
          const conditionItem = decompileFilterItem(item);
          if (conditionItem) acc.items.push(conditionItem);
          return acc;
        }
        const childNode: FilterTree = isFilterTree(item) ? item : { operator: 'OR', items: [item] };
        const childOperator =
          index === 0 && filter.operator === 'AND' && groupOperator === 'OR'
            ? 'OR'
            : filter.operator;
        stack.push([childNode, childOperator]);
        return acc;
      },
      createConditionGroup({ groupOperator, itemOperator: 'OR' }),
    );

    filterGroup.items.reverse();
    filterGroup.items = mergeGroupSentimentItems(filterGroup.items);

    if (filterGroup.items.length > 0) groups.push(filterGroup);
  }

  return groups.length > 0 ? groups : [createEmptyFilterGroup()];
}

function getContentTagIdsFromFilterTree(filters: FilterTree): number[] {
  return filters.items.flatMap((item) => {
    if (isFilterTree(item)) return getContentTagIdsFromFilterTree(item);
    if (item.key === PERFORMANCE_ALERT_FILTER_KEY.CONTENT_TAGS) return item.value;
    return [];
  });
}

export function getPerformanceAlertContentTagIds(
  filters?: PerformanceAlertFilters | null,
): number[] {
  if (!filters) return [];
  return getContentTagIdsFromFilterTree(filters as FilterTree);
}
